import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {NzModalRef} from 'ng-zorro-antd/modal';
import {NzMessageService} from 'ng-zorro-antd/message';
import {MfaService} from '../../../pages/login/service/mfa.service';

/** Các bước trong wizard setup 2FA */
type SetupStep = 'idle' | 'qr' | 'verify';

@Component({
  selector: 'app-mfa-manager',
  templateUrl: './mfa-manager.component.html',
  styleUrls: ['./mfa-manager.component.scss']
})
export class MfaManagerComponent implements OnInit {

  /** Trạng thái 2FA hiện tại của user */
  mfaEnabled = false;
  statusLoading = true;

  /** Luồng Setup */
  setupStep: SetupStep = 'idle';
  qrCodeUrl: string = null;
  setupLoading = false;
  setupForm: FormGroup;

  /** Luồng Disable */
  showDisableForm = false;
  disableForm: FormGroup;
  disableLoading = false;

  errorMessage: string = null;

  constructor(
    private fb: FormBuilder,
    private modalRef: NzModalRef,
    private mfaService: MfaService,
    private message: NzMessageService
  ) {}

  ngOnInit(): void {
    this.setupForm = this.fb.group({
      otp: [null, [Validators.required, Validators.pattern('^[0-9]{6}$')]]
    });
    this.disableForm = this.fb.group({
      otp: [null, [Validators.required, Validators.pattern('^[0-9]{6}$')]]
    });
    this.loadStatus();
  }

  // ── Trạng thái 2FA ──────────────────────────────────────────

  loadStatus(): void {
    this.statusLoading = true;
    this.mfaService.getMfaStatus().subscribe(
      res => {
        this.statusLoading = false;
        this.mfaEnabled = res?.data?.enabled === true;
      },
      () => {
        this.statusLoading = false;
      }
    );
  }

  // ── Bật 2FA: Bước 1 – lấy QR code ───────────────────────────

  startSetup(): void {
    this.errorMessage = null;
    this.setupStep = 'idle';
    this.setupLoading = true;
    this.setupForm.reset();
    this.mfaService.setupMfa().subscribe(
      res => {
        this.setupLoading = false;
        if (res?.data?.qrCodeUrl) {
          // Backend trả về dạng chuỗi otpauth://totp/...
          // Cần dùng dịch vụ bên ngoài để tạo hình ảnh QR code
          const rawUri = res.data.qrCodeUrl;
          this.qrCodeUrl = 'https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=' + encodeURIComponent(rawUri);
          this.setupStep = 'qr';
        } else {
          this.errorMessage = res?.message ?? 'Không thể khởi tạo 2FA. Vui lòng thử lại.';
        }
      },
      err => {
        this.setupLoading = false;
        this.errorMessage = err?.message ?? 'Có lỗi xảy ra. Vui lòng thử lại.';
      }
    );
  }

  goToVerifyStep(): void {
    this.setupStep = 'verify';
    this.setupForm.reset();
    this.errorMessage = null;
  }

  // ── Bật 2FA: Bước 2 – xác nhận OTP ─────────────────────────

  confirmSetup(): void {
    if (this.setupForm.invalid) {
      Object.values(this.setupForm.controls).forEach(c => {
        c.markAsDirty();
        c.updateValueAndValidity({onlySelf: true});
      });
      return;
    }
    this.setupLoading = true;
    this.errorMessage = null;
    const otp = this.setupForm.get('otp').value;
    this.mfaService.verifySetup(otp).subscribe(
      () => {
        this.setupLoading = false;
        this.mfaEnabled = true;
        this.setupStep = 'idle';
        this.qrCodeUrl = null;
        this.message.success('Kích hoạt 2FA thành công!');
      },
      err => {
        this.setupLoading = false;
        this.errorMessage = err?.message ?? 'Mã OTP không hợp lệ. Vui lòng thử lại.';
      }
    );
  }

  cancelSetup(): void {
    this.setupStep = 'idle';
    this.qrCodeUrl = null;
    this.errorMessage = null;
    this.setupForm.reset();
  }

  // ── Tắt 2FA ─────────────────────────────────────────────────

  startDisable(): void {
    this.showDisableForm = true;
    this.errorMessage = null;
    this.disableForm.reset();
  }

  confirmDisable(): void {
    if (this.disableForm.invalid) {
      Object.values(this.disableForm.controls).forEach(c => {
        c.markAsDirty();
        c.updateValueAndValidity({onlySelf: true});
      });
      return;
    }
    this.disableLoading = true;
    this.errorMessage = null;
    const otp = this.disableForm.get('otp').value;
    this.mfaService.disableMfa(otp).subscribe(
      () => {
        this.disableLoading = false;
        this.mfaEnabled = false;
        this.showDisableForm = false;
        this.message.success('Đã tắt xác thực 2 lớp.');
      },
      err => {
        this.disableLoading = false;
        this.errorMessage = err?.message ?? 'Mã OTP không hợp lệ. Vui lòng thử lại.';
      }
    );
  }

  cancelDisable(): void {
    this.showDisableForm = false;
    this.disableForm.reset();
    this.errorMessage = null;
  }

  // ── Đóng modal ───────────────────────────────────────────────

  close(): void {
    this.modalRef.close();
  }

  /** Chỉ cho phép nhập số */
  onOtpKeyPress(event: KeyboardEvent): boolean {
    return /[0-9]/.test(event.key);
  }
}
